import React from 'react';

/**
 * One Cognitive Agents plan.
 *
 * Ports the structure of the marketing site's PricingPlans.tsx — name, large
 * price, description, tick-list, full-width CTA at the foot — into the
 * dashboard's light palette. That component is Tailwind + shadcn on a dark
 * glass-card theme and there is no Tailwind in client/, so this rebuilds the
 * layout in the inline-style vocabulary the dashboard already uses rather than
 * copying its classes.
 *
 * Purely presentational. Shared by the Billing page and the account-setup
 * wizard so the two can never disagree about what a plan costs.
 */

const DEFAULT_ACCENT = '#132848';

const money = (minorUnits, currency) => {
    const symbol = currency === 'USD' ? '$' : '₹';
    return `${symbol}${((Number(minorUnits) || 0) / 100).toLocaleString()}`;
};

// A plan that charges nothing is "Free", not "$0" — and Free is not a rate, so
// the card drops the "/ month" beside it. Currency-agnostic on purpose: ₹0 and
// $0 are the same offer to the reader.
const isFree = (minorUnits) => (Number(minorUnits) || 0) === 0;

const makeStyles = (PRIMARY) => ({
    card: (current) => ({
        display: 'flex',
        flexDirection: 'column',
        background: '#fff',
        border: current ? `2px solid ${PRIMARY}` : '1px solid #E4E7EC',
        borderRadius: 14,
        // Keep the inner box identical whether or not the 2px current-plan
        // border is drawn, or cards jump by a pixel as selection moves.
        padding: current ? 27 : 28,
        position: 'relative',
        boxShadow: '0 1px 2px rgba(16,24,40,0.05)',
        height: '100%',
        boxSizing: 'border-box',
    }),
    badge: {
        position: 'absolute',
        top: -11,
        left: '50%',
        transform: 'translateX(-50%)',
        background: PRIMARY,
        color: '#fff',
        fontSize: 11,
        fontWeight: 600,
        padding: '3px 10px',
        borderRadius: 999,
        whiteSpace: 'nowrap',
    },
    popularBadge: {
        position: 'absolute',
        top: -11,
        left: '50%',
        transform: 'translateX(-50%)',
        background: PRIMARY,
        color: '#fff',
        fontSize: 11,
        fontWeight: 600,
        padding: '3px 10px',
        borderRadius: 999,
        whiteSpace: 'nowrap',
    },
    name: { fontSize: 18, fontWeight: 600, color: '#101828', marginBottom: 6 },
    wasPrice: { fontSize: 20, color: '#98A2B3', textDecoration: 'line-through', fontWeight: 500 },
    offer: {
        display: 'inline-block',
        marginTop: 8,
        padding: '2px 10px',
        borderRadius: 999,
        background: `${PRIMARY}14`,
        border: `1px solid ${PRIMARY}59`,
        color: PRIMARY,
        fontSize: 11,
        fontWeight: 600,
    },
    priceRow: { display: 'flex', alignItems: 'baseline', gap: 6 },
    price: { fontSize: 36, fontWeight: 700, color: '#101828', lineHeight: 1.15, letterSpacing: '-0.02em' },
    period: { fontSize: 14, color: '#667085' },
    saving: {
        display: 'inline-block',
        marginTop: 8,
        padding: '2px 10px',
        borderRadius: 999,
        background: '#ECFDF3',
        border: '1px solid #ABEFC6',
        color: '#067647',
        fontSize: 11,
        fontWeight: 600,
    },
    // No minHeight: an empty description reserved 36px of dead air between the
    // price and the feature list on every plan without one.
    desc: { fontSize: 13, lineHeight: 1.5, color: '#667085', marginTop: 12 },
    list: { listStyle: 'none', padding: 0, margin: '20px 0 0', flex: 1 },
    li: { display: 'flex', alignItems: 'flex-start', gap: 10, fontSize: 14, color: '#344054', padding: '5px 0' },
    cta: (current, busy) => ({
        width: '100%',
        marginTop: 24,
        padding: '11px 16px',
        borderRadius: 8,
        fontSize: 14,
        fontWeight: 600,
        cursor: current || busy ? 'default' : 'pointer',
        border: current ? '1px solid #D0D5DD' : 'none',
        background: current ? '#F9FAFB' : PRIMARY,
        color: current ? '#667085' : '#fff',
        opacity: busy && !current ? 0.7 : 1,
    }),
});

function Tick({ accent }) {
    return (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" style={{ flexShrink: 0, marginTop: 2 }}>
            <path d="M5 13L9 17L19 7" stroke={accent} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
    );
}

export default function PlanCard({
    plan, onSelect, busy = false, ctaLabel = 'Choose plan', accent = DEFAULT_ACCENT,
    // The account-setup wizard shows the plan the account is ALREADY on as a
    // live choice: it was auto-assigned at signup, nobody picked it, and
    // greying it out as "Your current plan" leaves a new user with a card they
    // cannot act on. Everywhere else, current still means settled.
    allowCurrent = false,
}) {
    const styles = makeStyles(accent);
    const showAsCurrent = Boolean(plan.isCurrent) && !allowCurrent;
    // A string for a uniform row, or a function when the label depends on the
    // plan — the wizard says "Get Started" on the free plan and "Choose plan"
    // on the rest, because those are different acts: one costs nothing and
    // completes immediately, the other opens a checkout.
    const label = typeof ctaLabel === 'function' ? ctaLabel(plan) : ctaLabel;
    const yearly = plan.interval === 'yearly';
    // Only a "was" price strictly above what is charged is a discount. The
    // server refuses the inverted case, but a card must not render a negative
    // saving if a legacy row ever carries one.
    const discounted = Number(plan.originalPriceMinorUnits) > Number(plan.chargeMinorUnits);
    const free = isFree(plan.chargeMinorUnits);

    // The attached master features ARE the plan's selling points — that is what
    // the admin curated them for. Only when a plan has none do we fall back to
    // the raw allowance, so a card is never blank; showing both would repeat
    // "1,000 credits / month" twice on any plan that lists it itself.
    // A zero allowance is dropped rather than printed: plans outside the agent
    // catalogue carry no `fees` block, and "0 runs a month" reads as a limit the
    // customer has been given, not as an absent number.
    const curated = Array.isArray(plan.highlights) ? plan.highlights : [];
    const highlights = curated.length > 0 ? curated : [
        Number(plan.includedRuns) > 0 && `${Number(plan.includedRuns).toLocaleString()} runs a month`,
        Number(plan.includedCredits) > 0 && `${Number(plan.includedCredits).toLocaleString()} AI credits a month`,
    ].filter(Boolean);

    return (
        <div style={styles.card(plan.isCurrent)}>
            {/* Current plan wins the slot: what you are on matters more than
                what we would like to sell you. */}
            {plan.isCurrent
                ? <span style={styles.badge}>Current plan</span>
                : plan.isPopular ? <span style={styles.popularBadge}>Most Popular</span> : null}

            <div style={styles.name}>{plan.name}</div>

            <div style={styles.priceRow}>
                <span style={styles.price}>{free ? 'Free' : money(plan.chargeMinorUnits, plan.currency)}</span>
                {discounted && (
                    <span style={styles.wasPrice}>{money(plan.originalPriceMinorUnits, plan.currency)}</span>
                )}
                {/* A plan whose billing period isn't the interval it is priced
                    in — the free trial bills nothing and lasts 15 days — says so
                    itself rather than being described as monthly. */}
                {/* Derived from the plan's own interval, never overridden.
                    The override slot used to exist and both callers filled it
                    with a hardcoded "15 days" — copied from a Razorpay trial
                    that no longer exists, so the card contradicted the renewal
                    date printed beside it. One source, and it is the plan. */}
                {!free && <span style={styles.period}>/ {yearly ? 'year' : 'month'}</span>}
            </div>

            {discounted && <div><span style={styles.offer}>Limited Time Offer</span></div>}

            {yearly && plan.annualDiscountPct > 0 && (
                <div>
                    <span style={styles.saving}>
                        {Math.round(plan.annualDiscountPct * 100)}% off paying monthly
                    </span>
                </div>
            )}

            {plan.description ? <div style={styles.desc}>{plan.description}</div> : null}

            <ul style={styles.list}>
                {highlights.map((h) => (
                    <li key={h} style={styles.li}><Tick accent={accent} />{h}</li>
                ))}
            </ul>

            {onSelect && (
                <button
                    type="button"
                    style={styles.cta(showAsCurrent, busy)}
                    disabled={showAsCurrent || busy}
                    onClick={() => onSelect(plan)}
                >
                    {showAsCurrent ? 'Your current plan' : (busy ? 'Opening checkout…' : label)}
                </button>
            )}
        </div>
    );
}
